/**
 * Access & entitlements. ROARDRIVE is open: anyone enters, picks an engine and
 * either runs the DEMO (pit limiter at DEMO_LIMIT_KMH, DEMO_SECONDS per car per
 * day) or buys the car (one-time, Stripe Checkout via QR).
 *
 * Purchases are recorded server-side against this device and the email the
 * buyer typed at checkout; signing in with that email (OTP) restores them on any
 * Tesla. Admin = signed in with an email the server lists in ADMIN_EMAILS.
 */
import { getCar, legacyIdsOf } from '../cars/registry';

const DEVICE = 'roardrive:device:v1';
const ACCOUNT = 'roardrive:account:v1';
const PURCH = 'roardrive:purchases:v1';
const DEMO = 'roardrive:demo:v1';

/** Stable anonymous device id — purchases are recorded against it server-side. */
export function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE);
    if (!id) {
      // crypto, not Math.random: this id is what the server accepts as "this browser" for entitlements
      // and settings, so it must not be predictable from another id minted the same second
      id = 'dev_' + (crypto?.randomUUID ? crypto.randomUUID().replace(/-/g, '') : Math.random().toString(36).slice(2) + Date.now().toString(36));
      localStorage.setItem(DEVICE, id);
    }
    return id;
  } catch {
    return 'dev_ephemeral';
  }
}

/** Pull purchases recorded in Supabase for this device (+ its account) and merge them locally. */
export async function syncEntitlements(): Promise<void> {
  try {
    const r = await fetch(`/api/entitlements?device=${encodeURIComponent(getDeviceId())}`);
    if (!r.ok) return;
    const j = (await r.json()) as { cars: string[] };
    for (const c of j.cars ?? []) addPurchase(c);
  } catch {
    /* offline / dev server — local purchases still apply */
  }
}

export interface AccountInfo {
  email: string;
  userId?: string;
  /** relay room shared by this account's phone app and car browser */
  room?: string;
  /** the key that room needs to open the relay socket (server-computed) */
  roomKey?: string;
  /** server says this email is an admin; token authorises /api/admin/* */
  admin?: boolean;
  adminToken?: string;
}

export function getAccount(): AccountInfo | null {
  try {
    const raw = localStorage.getItem(ACCOUNT);
    if (!raw) return null;
    if (raw.startsWith('{')) return JSON.parse(raw) as AccountInfo;
    return { email: raw }; // legacy: plain email string
  } catch {
    return null;
  }
}

export function getAccountEmail(): string | null {
  return getAccount()?.email ?? null;
}

export function setAccount(info: AccountInfo | null): void {
  try {
    if (info) localStorage.setItem(ACCOUNT, JSON.stringify(info));
    else localStorage.removeItem(ACCOUNT);
  } catch {
    /* ignore */
  }
}

/** Everything this browser knows about the account — used after log out / delete. Device id and settings stay. */
export function clearAccountData(): void {
  try {
    localStorage.removeItem(ACCOUNT);
    localStorage.removeItem(PURCH);
    localStorage.removeItem('roardrive:grants:v1');
  } catch {
    /* ignore */
  }
}

/** Delete the account on the server (GDPR). Resolves true when the server confirmed. */
export async function deleteAccount(): Promise<boolean> {
  const email = getAccountEmail();
  if (!email) return false;
  try {
    const r = await fetch('/api/account/delete', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ deviceId: getDeviceId(), email }) });
    return r.ok;
  } catch {
    return false;
  }
}

/** Signed in with an admin email (verified by the server at sign-in). */
export function isAdmin(): boolean {
  const a = getAccount();
  return !!(a?.admin && a.adminToken);
}

export function getAdminToken(): string | null {
  return getAccount()?.adminToken ?? null;
}

export function getPurchases(): string[] {
  try {
    const raw = localStorage.getItem(PURCH);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export function addPurchase(carId: string): void {
  try {
    const set = new Set(getPurchases());
    set.add(carId);
    localStorage.setItem(PURCH, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

/** Admin → every car. Free cars always. Otherwise only purchased cars; the rest run as DEMO. */
export function isCarUnlocked(carId: string): boolean {
  if (isAdmin()) return true;
  if (getCar(carId)?.free) return true;
  // a purchase stored under the name the car had when it was bought still counts (the Porsche was
  // porsche-911-rsr-copy for its first 28 sales); the server sends the current name since 2026-09-02,
  // but a car browser that has not synced yet only has what it saved back then
  const bought = getPurchases();
  return bought.includes(carId) || legacyIdsOf(carId).some((old) => bought.includes(old));
}

/** Car that hasn't been purchased → playable as a demo (pit limiter + time budget). */
export function isDemoCar(carId: string): boolean {
  return !isCarUnlocked(carId);
}

// ---- DEMO budget: DEMO_SECONDS of engine time per car per day (resets at local midnight) ----
// Marc, 2026-08-31: 100. It was 59 because the real GT3 pit-limiter page reads Limit 59 in his
// photographs, which was the right number for a pit lane and the wrong one for a taste of the car.
export const DEMO_LIMIT_KMH = 100;
export const DEMO_SECONDS = 120;

// ---- THE OPEN WEEK (Marc, 2026-09-02): the three Italians arrive, and for one week the road demo is
// five minutes a day with the pit limiter OFF, so a V12 can be heard at the top of third rather than
// held at 100 in second. It ends by itself at this instant — the same instant server.mjs uses for its
// ledger (DEMO_PROMO_UNTIL) — and everything below falls back to the two minutes and the 100.
export const DEMO_PROMO_UNTIL = Date.parse('2026-09-09T23:59:59+02:00');
export const DEMO_PROMO_SECONDS = 300;
export function demoPromoOn(): boolean { return Date.now() < DEMO_PROMO_UNTIL; }
/** today's road budget per car, in seconds */
export function demoSeconds(): number { return demoPromoOn() ? DEMO_PROMO_SECONDS : DEMO_SECONDS; }
/** today's demo pit limiter — null while the open week runs */
export function demoLimitKmh(): number | null { return demoPromoOn() ? null : DEMO_LIMIT_KMH; }

interface DemoLedger {
  day: string;
  used: Record<string, number>;
}
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}
function readDemo(): DemoLedger {
  try {
    const raw = localStorage.getItem(DEMO);
    const l = raw ? (JSON.parse(raw) as DemoLedger) : null;
    if (l && l.day === today() && l.used) return l;
  } catch {
    /* ignore */
  }
  return { day: today(), used: {} };
}

/** Seconds of demo already used today for this car. */
export function demoUsedSeconds(carId: string): number {
  return readDemo().used[carId] ?? 0;
}

export function demoLeftSeconds(carId: string): number {
  return Math.max(0, demoSeconds() - demoUsedSeconds(carId));
}

/** Add `seconds` of demo time for this car; returns seconds left. */
export function demoConsume(carId: string, seconds: number): number {
  const l = readDemo();
  l.used[carId] = (l.used[carId] ?? 0) + seconds;
  try {
    localStorage.setItem(DEMO, JSON.stringify(l));
  } catch {
    /* ignore */
  }
  return Math.max(0, DEMO_SECONDS - l.used[carId]);
}

/** Bring the local demo ledger down to what the SERVER says is left (never up: a wiped browser must not
 *  be able to buy itself more time by forgetting). */
export function capDemoLeft(carId: string, leftSeconds: number): void {
  const used = Math.max(0, DEMO_SECONDS - Math.max(0, leftSeconds));
  if (used <= demoUsedSeconds(carId)) return;
  const l = readDemo();
  l.used[carId] = used;
  try { localStorage.setItem(DEMO, JSON.stringify(l)); } catch { /* ignore */ }
}
